import React, { useEffect, useRef, useState, useCallback } from 'react';
import api from './api';
import { Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import BottomPanel from './BottomPanel';

let isAutodeskInitialized = false;

const STEEL_DENSITY_KG_PER_M3 = 7850;
const MAX_REBAR_PER_HOST = 500;

const BIMViewer = ({ urn, onElementSelect, onTreeReady }) => {
  const viewerContainer = useRef(null);
  const viewer = useRef(null);
  const bubbleDataRef = useRef(null);

  const callbacksRef = useRef({ onElementSelect, onTreeReady });
  const [isTreeReady, setIsTreeReady] = useState(false);
  const [selectedElement, setSelectedElement] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const selectedElementRef = useRef(null);
  const rebarRegistryRef = useRef(new Map());

  useEffect(() => {
    callbacksRef.current = { onElementSelect, onTreeReady };
  }, [onElementSelect, onTreeReady]);

  useEffect(() => {
    selectedElementRef.current = selectedElement;
  }, [selectedElement]);

  // =================================================================
  // 1. Fly-to and isolate function
  // =================================================================
  useEffect(() => {
    window.selectBIMElement = (dbId) => {
      if (viewer.current) {
        viewer.current.clearSelection();
        viewer.current.isolate([dbId]);
        viewer.current.fitToView([dbId]);
        viewer.current.select([dbId]);
      }
    };
    return () => { delete window.selectBIMElement; };
  }, []);

  // =================================================================
  // 2. Interactive button functions (MR & SweetAlert)
  // =================================================================
  useEffect(() => {
    window.triggerSAPMR = async (elementName, elementId, category, quantity, unit, assemblyCode, elementGuid, rebarWeightKg) => {
      const toastId = toast.loading(`Sending material request/reservation to the warehouse for element [${elementName}]...`);
      try {
        const safeGuid = (elementGuid && elementGuid !== 'Unknown') ? elementGuid : null;

        const items = [{
          element_id: elementId.toString(),
          element_guid: safeGuid,
          category: category || "General",
          assembly_code: (assemblyCode && assemblyCode !== 'N/A') ? assemblyCode : null,
          quantity: quantity.toString(),
          unit: unit || "EA",
          plant: "1010"
        }];

        const rebarKg = parseFloat(rebarWeightKg);
        if (!isNaN(rebarKg) && rebarKg > 0) {
          items.push({
            element_id: elementId.toString(),
            element_guid: safeGuid,
            category: "Structural Rebar",
            assembly_code: null,
            quantity: rebarKg.toFixed(1),
            unit: "KG",
            plant: "1010"
          });
        }

        const payload = { project_name: "BIM Integrated Project", items };
        const response = await api.post('/v1/create-mr', payload);
        if (response.data.simulated) {
          toast(`🧪 Simulation mode: the request was recorded locally only and was not actually sent to SAP.\nReference number: ${response.data.sap_mr_number}`, { id: toastId, duration: 7000, icon: '🧪', style: { border: '1px solid #f59e0b', padding: '16px', background: '#fffbeb' } });
        } else {
          toast.success(`Material request sent to the warehouse successfully!\nMR Number: ${response.data.sap_mr_number}`, { id: toastId, duration: 6000, icon: '📦', style: { border: '1px solid #22c55e', padding: '16px' } });
        }
      } catch (error) {
        const errorMsg = error.response?.data?.detail || error.message || 'Failed to connect to the SAP warehouse server';
        toast.error(`Request rejected by the warehouse:\n${errorMsg}`, { id: toastId, duration: 8000, style: { border: '1px solid #ef4444', padding: '16px', maxWidth: '500px' } });
      }
    };

    window.viewSAPDetails = () => {
      if (!bubbleDataRef.current) return;
      const { data } = bubbleDataRef.current;
      const sap = data.sapData || {};

      const materials = [
        ...(sap.goods_issued || []).map(m => ({ material: m.material, qty: m.qty, status: m.status || 'Issued' })),
        ...(sap.pending_mrs || []).map(m => ({ material: m.material, qty: m.qty, status: m.status || 'Pending' })),
      ];

      let tableRows = '';
      if (materials.length === 0) {
          tableRows = `<tr><td colspan="3" style="text-align: center; padding: 20px; color: #64748b; font-weight: bold;">No materials issued or requests recorded for this asset in SAP</td></tr>`;
      } else {
          materials.forEach(m => {
              tableRows += `
              <tr>
                <td style="padding: 10px; border: 1px solid #e2e8f0; font-weight: bold; color: #0f172a;">${m.material}</td>
                <td style="padding: 10px; border: 1px solid #e2e8f0; color: #334155;">${m.qty}</td>
                <td style="padding: 10px; border: 1px solid #e2e8f0;">
                    <span style="background: #fef9c3; color: #854d0e; padding: 2px 6px; border-radius: 4px; font-size: 12px; font-weight: bold;">${m.status}</span>
                </td>
              </tr>
              `;
          });
      }

      Swal.fire({
        title: 'Engineering & Supply Data (5D BIM)',
        html: `
          <div style="text-align: left; font-family: sans-serif; margin-top: 10px;">
            <div style="background: #f8fafc; padding: 12px; border-radius: 8px; border: 1px solid #e2e8f0; margin-bottom: 16px;">
                <p style="margin: 0 0 6px 0; color: #0f172a;"><strong>Element:</strong> ${data.name}</p>
                <p style="margin: 0 0 6px 0; color: #64748b; font-size: 14px;"><strong>Revit ID:</strong> ${data.dbId}</p>
                <p style="margin: 0 0 6px 0; color: #64748b; font-size: 14px;"><strong>Location/Level:</strong> ${data.level}</p>
                <p style="margin: 0 0 6px 0; color: #64748b; font-size: 14px;"><strong>Phase:</strong> ${data.phase}</p>
                <p style="margin: 0; color: #2563eb; font-size: 14px; font-weight: bold;"><strong>Required Qty:</strong> ${data.quantity} ${data.uom}</p>
            </div>
            <h4 style="margin: 0 0 10px 0; color: #0f172a; font-size: 15px; border-bottom: 2px solid #e2e8f0; padding-bottom: 5px;">Material Log</h4>
            <table style="width: 100%; border-collapse: collapse; font-size: 14px; background: #ffffff;">
              <thead>
                <tr style="background: #f1f5f9; text-align: left;">
                  <th style="padding: 10px; border: 1px solid #cbd5e1; color: #475569;">Material Code</th>
                  <th style="padding: 10px; border: 1px solid #cbd5e1; color: #475569;">Quantity</th>
                  <th style="padding: 10px; border: 1px solid #cbd5e1; color: #475569;">Status</th>
                </tr>
              </thead>
              <tbody>
                ${tableRows}
              </tbody>
            </table>
          </div>
        `,
        icon: 'info',
        confirmButtonText: 'Close',
        confirmButtonColor: '#2563eb',
        width: '650px',
        customClass: { popup: 'rounded-2xl shadow-2xl' }
      });
    };

    return () => {
        delete window.triggerSAPMR;
        delete window.viewSAPDetails;
    };
  }, []);

  // =================================================================
  // 3. Bubble design and positioning
  // =================================================================
  const renderBubble = useCallback(() => {
    if (!bubbleDataRef.current || !viewer.current) return;

    const { data, center3D } = bubbleDataRef.current;
    const screenPoint = viewer.current.worldToClient(center3D);

    if (!screenPoint || isNaN(screenPoint.x) || isNaN(screenPoint.y)) return;

    let bubble = document.getElementById(`bubble-sap-active`);
    if (!bubble) {
        bubble = document.createElement('div');
        bubble.id = `bubble-sap-active`;
        viewer.current.container.appendChild(bubble);
    }

    let topY = screenPoint.y - 30;
    if (topY < 10) topY = 10;

    bubble.style.position = 'absolute';
    bubble.style.zIndex = '999999';
    bubble.style.left = `${screenPoint.x}px`;
    bubble.style.top = `${topY}px`;
    bubble.style.transform = 'translate(-50%, -100%)';
    bubble.style.pointerEvents = 'auto';
    bubble.style.transition = 'left 0.1s ease-out, top 0.1s ease-out';
    bubble.style.borderTop = `5px solid #2563eb`;
    bubble.style.backgroundColor = 'rgba(255, 255, 255, 0.98)';
    bubble.style.backdropFilter = 'blur(10px)';
    bubble.style.padding = '0';
    bubble.style.borderRadius = '12px';
    bubble.style.boxShadow = '0 25px 50px -12px rgba(0, 0, 0, 0.3)';
    bubble.style.minWidth = '240px';

    if (screenPoint.z > 1) {
        bubble.style.display = 'none';
        return;
    } else {
        bubble.style.display = 'block';
    }

    if (data.isLoading) {
        bubble.innerHTML = `
            <div style="padding: 24px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 12px;">
                <div style="width: 24px; height: 24px; border: 3px solid #e2e8f0; border-top-color: #2563eb; border-radius: 50%; animation: spin 1s linear infinite;"></div>
                <style>@keyframes spin { to { transform: rotate(360deg); } }</style>
                <span style="font-size: 12px; font-weight: bold; color: #64748b;">Fetching Live SAP Data...</span>
            </div>
        `;
        return;
    }

    if (data.error) {
        bubble.innerHTML = `
            <div style="padding: 16px; text-align: center;">
                <span style="font-size: 14px; font-weight: bold; color: #dc2626;">Failed to load ERP data</span>
                <p style="font-size: 11px; color: #64748b; margin-top: 4px;">Element ID: ${data.dbId}</p>
            </div>
        `;
        return;
    }

    const sap = data.sapData || {};

    bubble.innerHTML = `
        <div style="padding: 16px 16px 12px 16px; border-bottom: 1px solid #f1f5f9;">
            <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 8px;">
                <span style="font-size: 16px; font-weight: 900; color: #0f172a; max-width: 180px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">${data.name.split(' ')[0]}</span>
                <span style="font-size: 10px; padding: 4px 8px; background-color: #fef9c3; color: #854d0e; border-radius: 6px; font-weight: bold; letter-spacing: 0.5px; text-transform: uppercase;">
                    ${data.assemblyCode && data.assemblyCode !== 'N/A' ? data.assemblyCode : data.phase}
                </span>
            </div>
            <div style="font-size: 11px; color: #64748b; font-weight: 600; margin-bottom: 4px;">Location: <span style="color:#334155">${data.level}</span></div>
            <div style="font-size: 11px; color: #64748b; font-weight: 600;">Quantity: <span style="color:#2563eb; font-weight: 900;">${data.quantity} ${data.uom}</span></div>
            ${data.rebarWeightKg > 0 ? `<div style="font-size: 11px; color: #64748b; font-weight: 600; margin-top: 4px;">Reinforcement: <span style="color:#b45309; font-weight: 900;">${data.rebarWeightKg} KG</span></div>` : ''}
        </div>

        <div style="padding: 12px 16px; background-color: #f8fafc; border-bottom: 1px solid #e2e8f0;">
            <h4 style="font-size: 10px; color: #94a3b8; margin: 0 0 8px 0; text-transform: uppercase; letter-spacing: 1px;">SAP S/4HANA Live Data</h4>

            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px;">
                <span style="font-size: 12px; color: #475569; font-weight: 600;">Total Material Cost:</span>
                <span style="font-size: 13px; font-weight: 900; color: #dc2626;">${sap.total_material_cost || '0.00 SAR'}</span>
            </div>

            <div style="display: flex; justify-content: space-between; align-items: center;">
                <span style="font-size: 12px; color: #475569; font-weight: 600;">Pending MRs:</span>
                <span style="font-size: 12px; font-weight: 800; color: #2563eb; background: #dbeafe; padding: 2px 6px; border-radius: 4px;">${sap.pending_mrs ? sap.pending_mrs.length : 0} Active</span>
            </div>
        </div>

        <div style="padding: 12px 16px; background-color: #ffffff;">
            <div style="display: flex; justify-content: space-between; align-items: center;">
                <span style="font-size: 12px; color: #475569; font-weight: 600;">Goods Issued:</span>
                <span style="font-size: 12px; font-weight: 800; color: #ea580c;">${sap.goods_issued ? sap.goods_issued.length : 0} Docs</span>
            </div>
        </div>

        <div style="padding: 12px 16px; display: flex; gap: 8px; background-color: #f8fafc; border-top: 1px solid #f1f5f9;">
            <button onclick="window.triggerSAPMR('${data.name}', '${data.dbId}', '${data.category}', '${data.quantity}', '${data.uom}', '${data.assemblyCode}', '${data.guid}', '${data.rebarWeightKg || 0}')" style="flex: 1; background-color: #2563eb; color: white; border: none; padding: 8px; border-radius: 6px; font-size: 12px; font-weight: bold; cursor: pointer; transition: background 0.2s; box-shadow: 0 2px 4px rgba(37, 99, 235, 0.2);">
                + Request MR
            </button>
            <button onclick="window.viewSAPDetails()" style="flex: 1; background-color: #e2e8f0; color: #475569; border: none; padding: 8px; border-radius: 6px; font-size: 12px; font-weight: bold; cursor: pointer; transition: background 0.2s;">
                View Details
            </button>
        </div>
    `;
  }, []);

  const drawBubbleForElement = useCallback((dbId, elementData) => {
    const v = viewer.current;
    const THREE = window.THREE || window.Autodesk.Viewing.THREE;
    if (!v || !THREE) return;

    const tree = v.model.getInstanceTree();
    const fragList = v.model.getFragmentList();
    const bbox = new THREE.Box3();
    let hasFragments = false;

    tree.enumNodeFragments(dbId, (fragId) => {
      hasFragments = true;
      const fragBox = new THREE.Box3();
      fragList.getWorldBounds(fragId, fragBox);
      bbox.union(fragBox);
    }, true);

    if (!hasFragments || bbox.isEmpty()) {
        const b = document.getElementById(`bubble-sap-active`);
        if (b) b.style.display = 'none';
        return;
    }

    const center3D = new THREE.Vector3((bbox.min.x + bbox.max.x) / 2, (bbox.min.y + bbox.max.y) / 2, (bbox.min.z + bbox.max.z) / 2);
    bubbleDataRef.current = { data: elementData, center3D };
    renderBubble();
  }, [renderBubble]);

  const fetchRebarWeightKg = useCallback((rebarDbIds) => {
    return new Promise((resolve) => {
      if (!rebarDbIds || rebarDbIds.length === 0 || !viewer.current || !viewer.current.model) {
        resolve(0);
        return;
      }
      const idsToFetch = rebarDbIds.slice(0, MAX_REBAR_PER_HOST);
      try {
        viewer.current.model.getBulkProperties(
          idsToFetch,
          { propFilter: ['Weight', 'Volume'] },
          (results) => {
            let totalKg = 0;
            (results || []).forEach((r) => {
              const props = r.properties || [];
              const weightProp = props.find(p => p.displayName === 'Weight');
              const wVal = weightProp ? parseFloat(weightProp.displayValue) : NaN;
              if (!isNaN(wVal)) {
                totalKg += wVal;
                return;
              }
              const volProp = props.find(p => p.displayName === 'Volume');
              const vVal = volProp ? parseFloat(volProp.displayValue) : NaN;
              if (!isNaN(vVal)) {
                totalKg += vVal * STEEL_DENSITY_KG_PER_M3;
              }
            });
            resolve(totalKg);
          },
          () => resolve(0)
        );
      } catch (e) {
        console.error('fetchRebarWeightKg failed', e);
        resolve(0);
      }
    });
  }, []);

  const refreshSAPData = useCallback(async () => {
    const current = selectedElementRef.current;
    if (!current || !current.dbId) return;

    setIsRefreshing(true);
    const toastId = toast.loading('Refreshing data from SAP...');
    try {
      const res = await api.get(`/v1/asset-details/${current.dbId}`, {
        params: {
          category: current.category,
          element_guid: (current.guid && current.guid !== 'Unknown') ? current.guid : undefined,
        }
      });
      const updated = { ...current, isLoading: false, error: false, sapData: res.data };
      setSelectedElement(updated);
      drawBubbleForElement(current.dbId, updated);
      toast.success('Data refreshed from SAP successfully!', { id: toastId });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to refresh data from SAP', { id: toastId });
    } finally {
      setIsRefreshing(false);
    }
  }, [drawBubbleForElement]);

  // =================================================================
  // 4. Initialization and fast hierarchy extraction
  // =================================================================
  useEffect(() => {
    if (!urn || !viewerContainer.current) return;
    setIsTreeReady(false);

    const loadModel = () => {
      if (viewer.current) viewer.current.finish();

      viewer.current = new window.Autodesk.Viewing.GuiViewer3D(viewerContainer.current);
      viewer.current.start();
      viewer.current.setTheme('light-theme');

      const documentId = 'urn:' + urn;
      window.Autodesk.Viewing.Document.load(documentId, (doc) => {
        const viewables = doc.getRoot().getDefaultGeometry();

        viewer.current.addEventListener(window.Autodesk.Viewing.OBJECT_TREE_CREATED_EVENT, () => {
          setIsTreeReady(true);
          rebarRegistryRef.current = new Map();

          setTimeout(() => {
            const tree = viewer.current.model.getInstanceTree();
            if (tree && callbacksRef.current.onTreeReady) {

              const buildCleanTree = (nodeId, depth = 0) => {
                const name = tree.getNodeName(nodeId) || `Element ${nodeId}`;
                const lowerName = name.toLowerCase();

                if (lowerName.includes('rebar')) {
                    return { rebarOnly: true, id: nodeId };
                }

                if (
                  lowerName.includes('grid') ||
                  lowerName.includes('camera') ||
                  lowerName.includes('topography') ||
                  lowerName.includes('project info') ||
                  lowerName.includes('structural connection') ||
                  lowerName.includes('embed') ||
                  lowerName.includes('bolt') ||
                  lowerName === 'rooms' ||
                  lowerName === 'spaces' ||
                  lowerName === 'lines'
                ) {
                    return null;
                }

                if (depth === 1) {
                    const isWhitelistedCategory = lowerName.includes('wall') ||
                                                  lowerName.includes('floor') ||
                                                  lowerName.includes('column') ||
                                                  lowerName.includes('framing') ||
                                                  lowerName.includes('foundation') ||
                                                  lowerName.includes('roof') ||
                                                  lowerName.includes('beam') ||
                                                  lowerName.includes('slab') ||
                                                  lowerName.includes('structural');
                    if (!isWhitelistedCategory) {
                        return null;
                    }
                }

                const children = [];
                const rebarDbIds = [];
                let hasGeometry = false;

                tree.enumNodeFragments(nodeId, () => { hasGeometry = true; }, true);

                if (tree.getChildCount(nodeId) > 0) {
                    tree.enumNodeChildren(nodeId, (childId) => {
                        const childNode = buildCleanTree(childId, depth + 1);
                        if (childNode) {
                            if (childNode.rebarOnly) {
                                rebarDbIds.push(childNode.id);
                            } else {
                                children.push(childNode);
                            }
                        }
                    }, false);
                }

                if (children.length === 0 && !hasGeometry && rebarDbIds.length === 0) {
                    return null;
                }

                if (rebarDbIds.length > 0) {
                    rebarRegistryRef.current.set(nodeId, rebarDbIds);
                }

                return { id: nodeId, name, children, hasGeometry };
              };

              const cleanHierarchy = buildCleanTree(tree.getRootId(), 0);
              callbacksRef.current.onTreeReady(cleanHierarchy);
            }
          }, 500);
        });

        viewer.current.loadDocumentNode(doc, viewables).then(() => {
          viewer.current.addEventListener(window.Autodesk.Viewing.SELECTION_CHANGED_EVENT, (event) => {
            const dbId = event.dbIdArray[0];
            if (dbId) {
              viewer.current.getProperties(dbId, async (props) => {

                const propertiesList = props?.properties || [];
                const getProp = (name) => propertiesList.find(p => p.displayName === name)?.displayValue || 'N/A';

                const assemblyCode = getProp('Assembly Code');
                const typeName = getProp('Type Name');
                const structuralMat = getProp('Structural Material') || getProp('Material');

                const volumeStr = getProp('Volume');
                const areaStr = getProp('Area');
                const lengthStr = getProp('Length');

                const safeVolumeStr = String(volumeStr);
                const safeAreaStr = String(areaStr);
                const safeLengthStr = String(lengthStr);

                let numericQty = 1;
                let uom = "EA";

                if (volumeStr !== 'N/A') {
                    numericQty = parseFloat(volumeStr) || 1;
                    uom = safeVolumeStr.includes('ft³') || safeVolumeStr.includes('CF') ? 'FT3' : 'M3';
                } else if (areaStr !== 'N/A') {
                    numericQty = parseFloat(areaStr) || 1;
                    uom = safeAreaStr.includes('ft²') || safeAreaStr.includes('SF') ? 'FT2' : 'M2';
                } else if (lengthStr !== 'N/A') {
                    numericQty = parseFloat(lengthStr) || 1;
                    uom = safeLengthStr.includes('ft') || safeLengthStr.includes('\'') ? 'FT' : 'M';
                }

                const rawCategory = getProp('Category') !== 'N/A' ? getProp('Category') : 'Revit Element';

                const baseData = {
                  guid: props.externalId || 'Unknown',
                  dbId: dbId,
                  name: props.name || 'Unnamed Element',
                  category: rawCategory,
                  assemblyCode: assemblyCode !== 'N/A' ? assemblyCode : getProp('OmniClass Number'),
                  family: typeName !== 'N/A' ? typeName : getProp('Family'),
                  material: structuralMat !== 'N/A' ? structuralMat : 'Not Assigned',
                  level: getProp('Base Level') || getProp('Top Level') || getProp('Level') || 'Unknown Level',
                  phase: getProp('Phase Created') || 'New Construction',
                  volume: volumeStr !== 'N/A' ? safeVolumeStr : 'N/A',
                  area: areaStr !== 'N/A' ? safeAreaStr : 'N/A',
                  length: lengthStr !== 'N/A' ? safeLengthStr : 'N/A',
                  quantity: numericQty.toFixed(2),
                  uom: uom
                };

                const hostedRebarIds = rebarRegistryRef.current.get(dbId) || [];
                const rebarWeightKg = hostedRebarIds.length > 0 ? await fetchRebarWeightKg(hostedRebarIds) : 0;
                baseData.rebarWeightKg = rebarWeightKg > 0 ? Number(rebarWeightKg.toFixed(1)) : 0;

                viewer.current.fitToView([dbId]);
                drawBubbleForElement(dbId, { ...baseData, isLoading: true });

                try {
                  const res = await api.get(`/v1/asset-details/${dbId}`, {
                    params: {
                      category: rawCategory,
                      element_guid: baseData.guid !== 'Unknown' ? baseData.guid : undefined,
                    }
                  });
                  const fullData = { ...baseData, isLoading: false, sapData: res.data };
                  drawBubbleForElement(dbId, fullData);
                  setSelectedElement(fullData);
                } catch (err) {
                  const errData = { ...baseData, isLoading: false, error: true };
                  drawBubbleForElement(dbId, errData);
                  setSelectedElement(errData);
                }

                if (callbacksRef.current.onElementSelect) {
                    callbacksRef.current.onElementSelect(baseData);
                }
              });
            } else {
              setSelectedElement(null);
              bubbleDataRef.current = null;
              const b = document.getElementById(`bubble-sap-active`);
              if (b) b.style.display = 'none';
            }
          });
        });
      });
    };

    if (!isAutodeskInitialized) {
      const options = {
        env: 'AutodeskProduction',
        getAccessToken: async (callback) => {
          try {
            const res = await api.get('/v1/aps/token');
            if (res.data && res.data.access_token) {
                callback(res.data.access_token, res.data.expires_in);
            }
          } catch (error) {
            console.error("Failed to fetch APS Token", error);
          }
        }
      };

      window.Autodesk.Viewing.Initializer(options, () => {
        isAutodeskInitialized = true;
        loadModel();
      });
    } else {
      loadModel();
    }

    return () => {
      if (viewer.current) {
        viewer.current.finish();
        viewer.current = null;
      }
    };
  }, [urn, drawBubbleForElement, fetchRebarWeightKg]);

  useEffect(() => {
    if (!viewer.current) return;
    const onCameraChange = () => {
        if (bubbleDataRef.current) renderBubble();
    };
    viewer.current.addEventListener(window.Autodesk.Viewing.CAMERA_CHANGE_EVENT, onCameraChange);
    return () => {
        if (viewer.current) {
            viewer.current.removeEventListener(window.Autodesk.Viewing.CAMERA_CHANGE_EVENT, onCameraChange);
        }
    }
  }, [isTreeReady, renderBubble]);

  if (!urn) return null;

  return (
      <div className="w-full h-[650px] relative bg-slate-50 rounded-xl overflow-hidden border border-slate-200 shadow-inner flex flex-col">
          {!isTreeReady && (
              <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white/80 backdrop-blur-md">
                  <Loader2 className="w-12 h-12 text-blue-600 animate-spin mb-4" />
                  <span className="text-sm font-bold text-slate-700 tracking-wider">Rendering Digital Twin...</span>
              </div>
          )}
          <div ref={viewerContainer} className="w-full h-full absolute inset-0" />

          <BottomPanel
            elementData={selectedElement}
            onClose={() => setSelectedElement(null)}
            onRefresh={refreshSAPData}
            isRefreshing={isRefreshing}
            onCreateMR={(data) => {
                window.triggerSAPMR(data.name, data.dbId, data.category, data.quantity, data.uom, data.assemblyCode, data.guid, data.rebarWeightKg);
            }}
          />
      </div>
  );
};

export default BIMViewer;
